import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useEffect, useMemo } from "react"
import { DataResponse, MessageResponseDataSchema } from "schema"
import { z } from "zod"
import { getMessagesQueryKey, MessagesPage, messagesQuery } from "../lib/messagesQuery"

type Message = z.infer<typeof MessageResponseDataSchema>

type Options = {
  enabled?: boolean
  refetchInterval?: number
}

export function useMessages(conversationId: string | undefined, options: Options = {}) {
  const queryClient = useQueryClient()
  const enabled = options.enabled !== false && !!conversationId

  const infiniteQuery = useInfiniteQuery({
    ...messagesQuery(conversationId ?? "", { enabled }),
  })

  const pages = useMemo(() => infiniteQuery.data?.pages ?? [], [infiniteQuery])
  const messages = useMemo<Message[]>(() => {
    if (pages.length === 0) return []
    const all = pages.flatMap((p) => p.messages)
    return [...all].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
  }, [pages])

  const newestId = [...messages].reverse().find((m) => !m.id.startsWith("temp-"))?.id
  const oldestLoadedAt = messages.at(0)?.createdAt ?? null

  const pollQuery = useQuery({
    queryKey: [...getMessagesQueryKey(conversationId ?? ""), "after", newestId ?? ""],
    placeholderData: undefined,
    queryFn: async () => {
      const response = await axios.get<DataResponse<MessagesPage>>("/messages", {
        params: { conversationId, after: newestId, limit: 100 },
      })
      return response.data.data
    },
    enabled: enabled && !!newestId,
    refetchInterval: options.refetchInterval ?? 10000,
  })

  useEffect(() => {
    if (!conversationId || !pollQuery.data || pollQuery.data.messages.length === 0) return

    const newMessages = pollQuery.data.messages
    const key = getMessagesQueryKey(conversationId)

    queryClient.setQueryData<InfiniteData<MessagesPage>>(key, (oldData) => {
      if (!oldData) return oldData

      const knownIds = new Set(oldData.pages.flatMap((p) => p.messages.map((m) => m.id)))
      const toAppend = newMessages.filter((m) => !knownIds.has(m.id))
      if (toAppend.length === 0) return oldData

      const lastIndex = oldData.pages.length - 1
      const pagesCopy = oldData.pages.map((p, i) => {
        if (i !== lastIndex) return p
        const merged = [...p.messages, ...toAppend]
        return {
          ...p,
          messages: merged,
          pageInfo: {
            ...p.pageInfo,
            newestId: merged.at(-1)?.id ?? p.pageInfo.newestId,
          },
        }
      })

      return { ...oldData, pages: pagesCopy }
    })
  }, [pollQuery.data, conversationId, queryClient])

  return {
    messages,
    oldestLoadedAt: oldestLoadedAt ? new Date(oldestLoadedAt) : null,
    isLoading: infiniteQuery.isPending,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    isPollingNew: pollQuery.isFetching,
  }
}
